import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Trodo-styled hover tooltip — matches agent-analytics RunsHistogram popovers:
 * surface bg, border-default, shadow-token-lg, 11px Inter text.
 */
export default function TrodoTooltip({ content, children, placement = 'top', className = 'inline-flex' }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  const open = useCallback(() => {
    if (!content || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 768;

    if (placement === 'right') {
      const left = Math.min(r.right + 8, vw - 288);
      const top = Math.min(Math.max(r.top + r.height / 2, 8), vh - 8);
      setPos({ left, top, transform: 'translate(0, -50%)' });
      return;
    }

    const centerX = r.left + r.width / 2;
    const clampedX = Math.min(Math.max(centerX, 80), vw - 80);
    if (placement === 'bottom') {
      setPos({ left: clampedX, top: r.bottom + 6, transform: 'translate(-50%, 0)' });
    } else {
      setPos({ left: clampedX, top: Math.max(r.top - 6, 8), transform: 'translate(-50%, -100%)' });
    }
  }, [content, placement]);

  const close = useCallback(() => setPos(null), []);

  if (!content) return children;

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={open}
        onMouseLeave={close}
        onFocusCapture={open}
        onBlurCapture={close}
        className={className}
      >
        {children}
      </span>
      {pos && typeof document !== 'undefined' && createPortal(
        <div
          className="pointer-events-none fixed z-[130] max-w-[280px] rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 py-1.5 text-[11px] leading-snug text-[color:var(--text-primary)] shadow-token-lg"
          style={{ left: pos.left, top: pos.top, transform: pos.transform }}
        >
          {content}
        </div>,
        document.body,
      )}
    </>
  );
}
